import { useCommodityTypeService } from './commodityTypeService'
import { useTransactionService } from './transactionService'
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'
import type { CommodityType } from '@/types/CommodityType'
import type { TransactionRecord } from '@/types/Transaction'

/**
 * Initialization Service - Handles loading of initial data for forms and views
 * Provides a clean way to load all necessary data for complex forms
 */
export function useInitializationService() {
  const commodityTypeService = useCommodityTypeService()
  const transactionService = useTransactionService()
  const personStore = usePersonStore()
  const organizationStore = useOrganizationStore()

  return {
    /**
     * Initialize purchase form data
     * Loads all necessary data for the purchase form in parallel
     */
    async initializePurchaseForm(transactionId: string): Promise<{
      transaction: TransactionRecord
      commodityTypes: CommodityType[]
      persons: Person[]
      organizations: Organization[]
    }> {
      try {
        // Load all necessary data in parallel
        const [transaction, commodityTypes, persons, organizations] = await Promise.all([
          transactionService.startEditing(transactionId),
          commodityTypeService.getActiveCommodityTypes(),
          personStore.search().then((search) => search.recentlyUpdated(1000).run()),
          organizationStore.search().then((search) => search.recentlyUpdated(1000).run()),
        ])

        return {
          transaction,
          commodityTypes,
          persons,
          organizations,
        }
      } catch (error) {
        console.error('Failed to initialize purchase form:', error)
        throw new Error('Failed to initialize purchase form')
      }
    },

    /**
     * Initialize transaction form data
     * Loads basic data needed for transaction forms
     */
    async initializeTransactionForm(transactionId: string): Promise<{
      transaction: TransactionRecord
      commodityTypes: CommodityType[]
    }> {
      try {
        const [transaction, commodityTypes] = await Promise.all([
          transactionService.startEditing(transactionId),
          commodityTypeService.getActiveCommodityTypes(),
        ])

        return {
          transaction,
          commodityTypes,
        }
      } catch (error) {
        console.error('Failed to initialize transaction form:', error)
        throw new Error('Failed to initialize transaction form')
      }
    },

    /**
     * Initialize CRM data
     * Loads data needed for CRM operations
     */
    async initializeCrmData(): Promise<{
      persons: Person[]
      organizations: Organization[]
    }> {
      try {
        const [persons, organizations] = await Promise.all([
          personStore.search().then((search) => search.recentlyUpdated(1000).run()),
          organizationStore.search().then((search) => search.recentlyUpdated(1000).run()),
        ])

        return {
          persons,
          organizations,
        }
      } catch (error) {
        console.error('Failed to initialize CRM data:', error)
        throw new Error('Failed to initialize CRM data')
      }
    },
  }
}
